/**
 * cart-drawer.js
 */

(function () {
  'use strict';

  function formatMoney(cents) {
    // Usar la API nativa del navegador si está disponible — respeta la convención local
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
      || 'EUR';
    try {
      return new Intl.NumberFormat(navigator.language || 'es-ES', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      // Fallback a Shopify.formatMoney
      const format = (window.CartDrawerConfig && window.CartDrawerConfig.moneyFormat)
        || (window.Theme && window.Theme.moneyFormat);
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, format);
      }
      return (cents / 100).toFixed(2);
    }
  }

  class CartDrawer {
    constructor() {
      this.drawer   = document.querySelector('[data-cart-drawer]');
      this.overlay  = document.querySelector('[data-cart-drawer-overlay]');
      this.closeBtn = document.querySelector('[data-cart-drawer-close]');
      this.body     = document.querySelector('[data-cart-drawer-body]');
      this.emptyEl  = document.querySelector('[data-cart-drawer-empty]');
      this.footer   = document.querySelector('[data-cart-drawer-footer]');
      this.subtotal = document.querySelector('[data-cart-drawer-subtotal]');
      this._busy    = false;
      this._cart    = null; // estado del carrito en memoria

      if (!this.drawer) return;

      this._bindEvents();
      this._bindBodyDelegation();
      this._interceptCartLinks();
      this._interceptATCForms();
      this._interceptQuickBuy();
    }

    /* ----------------------------------------------------------
       OPEN / CLOSE
    ---------------------------------------------------------- */

    open() {
      // Calcular ancho del scrollbar antes de ocultarlo para evitar el salto del layout
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');

      this.overlay.classList.add('is-visible');
      document.body.classList.add('cart-drawer-open');
      requestAnimationFrame(() => {
        this.overlay.classList.add('is-open');
        this.drawer.classList.add('is-open');
        this.drawer.setAttribute('aria-hidden', 'false');
      });
      this.fetchCart();
    }

    close() {
      this.overlay.classList.remove('is-open');
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      document.documentElement.style.removeProperty('--scrollbar-width');
      this.overlay.addEventListener('transitionend', () => {
        this.overlay.classList.remove('is-visible');
      }, { once: true });
    }

    /* ----------------------------------------------------------
       FETCH CART
    ---------------------------------------------------------- */

    fetchCart() {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          this._cart = cart;
          this._renderCart(cart);
        })
        .catch(() => {
          this.body.innerHTML = '<p style="padding:16px">Error al cargar el carrito.</p>';
        });
    }

    /* ----------------------------------------------------------
       DELEGACIÓN — una sola vez en el constructor
       Lee la cantidad desde this._cart (memoria), no del DOM
    ---------------------------------------------------------- */

    _bindBodyDelegation() {
      this.body.addEventListener('click', (e) => {
        if (this._busy) return;

        const qtyBtn = e.target.closest('[data-cart-drawer-qty]');
        if (qtyBtn) {
          const lineIndex = parseInt(qtyBtn.dataset.line, 10); // 1-based
          const delta     = parseInt(qtyBtn.dataset.cartDrawerQty, 10);

          // Leer cantidad desde el estado en memoria, no del DOM
          const item    = this._cart && this._cart.items[lineIndex - 1];
          const current = item ? item.quantity : 1;
          const newQty  = Math.max(1, current + delta);

          this._setLineQty(lineIndex, newQty);
          return;
        }

        const removeBtn = e.target.closest('[data-cart-drawer-remove]');
        if (removeBtn) {
          this._setLineQty(parseInt(removeBtn.dataset.cartDrawerRemove, 10), 0);
        }
      });
    }

    /* ----------------------------------------------------------
       RENDER
    ---------------------------------------------------------- */

    _renderCart(cart) {
      const count = cart.item_count;

      const drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
      if (drawerCount) drawerCount.textContent = count > 0 ? count : '';

      if (count === 0) {
        this.body.style.display      = 'none';
        this.footer.style.display    = 'none';
        this.emptyEl.style.display   = '';
        return;
      }

      // Hay productos
      this.emptyEl.style.display = 'none';
      this.body.style.display    = '';
      this.footer.style.display  = '';

      if (this.subtotal) {
        this.subtotal.textContent = formatMoney(cart.total_price);
      }

      const items = cart.items.map((item, i) => this._renderItem(item, i + 1)).join('');
      this.body.innerHTML = `<ul class="cart-drawer--items">${items}</ul>`;
    }

    _renderItem(item, lineIndex) {
      const image = item.featured_image && item.featured_image.url
        ? `<img class="cart-drawer--item-image" src="${item.featured_image.url}&width=144" alt="${item.product_title}" loading="lazy">`
        : `<div class="cart-drawer--item-image placeholder"></div>`;

      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-drawer--item-variant">${item.variant_title}</p>`
        : '';

      const xIcon = `<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>`;

      return `
        <li class="cart-drawer--item" data-line="${lineIndex}">
          <a href="${item.url}" tabindex="-1">${image}</a>
          <div class="cart-drawer--item-details">
            <div class="cart-drawer--item-top">
              <a href="${item.url}" class="cart-drawer--item-title">${item.product_title}</a>
              <button class="cart-drawer--item-remove" data-cart-drawer-remove="${lineIndex}" type="button" aria-label="Eliminar">${xIcon}</button>
            </div>
            ${variant}
            <div class="cart-drawer--item-bottom">
              <div class="cart-drawer--item-qty">
                <button class="cart-drawer--qty-btn" data-line="${lineIndex}" data-cart-drawer-qty="-1" aria-label="Disminuir">−</button>
                <span class="cart-drawer--qty-value">${item.quantity}</span>
                <button class="cart-drawer--qty-btn" data-line="${lineIndex}" data-cart-drawer-qty="1" aria-label="Aumentar">+</button>
              </div>
              <span class="cart-drawer--item-price money">${formatMoney(item.line_price)}</span>
            </div>
          </div>
        </li>`;
    }

    /* ----------------------------------------------------------
       QUANTITY / REMOVE
    ---------------------------------------------------------- */

    _setLineQty(line, qty) {
      this._busy = true;

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: qty })
      })
        .then(r => r.json())
        .then(cart => {
          this._cart = cart; // actualizar estado en memoria
          this._renderCart(cart);
          this._updateHeaderDOM(cart);
        })
        .catch(err => console.error('CartDrawer: error cambiando cantidad', err))
        .finally(() => { this._busy = false; });
    }

    /* ----------------------------------------------------------
       HEADER COUNTER — DOM directo, sin eventos jQuery/nativos
    ---------------------------------------------------------- */

    _updateHeaderDOM(cart) {
      // Usar el mismo mecanismo que el tema: trigger 'cartcount:update' en $header
      // El handler del tema solo actualiza data-attribute y clase 'visible', sin textContent
      const $ = window.$ || window.jQuery;
      if (!$) return;
      const $header = $('[data-site-header]');
      if ($header.length) {
        $header.trigger('cartcount:update', { response: cart });
      }
    }

    /* ----------------------------------------------------------
       ADD TO CART INTERCEPT
    ---------------------------------------------------------- */

    _interceptATCForms() {
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!form.matches('[data-product-form]')) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const submitBtn = form.querySelector('[data-product-atc]');
        if (submitBtn) {
          submitBtn.classList.add('processing');
          submitBtn.disabled = true;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: new FormData(form)
        })
          .then(r => {
            if (!r.ok) return r.json().then(d => Promise.reject(d));
            return r.json();
          })
          .then(() => fetch('/cart.js').then(r => r.json()))
          .then(cart => {
            this._cart = cart;
            this._updateHeaderDOM(cart);
            this.open();
          })
          .catch(err => {
            console.error('CartDrawer: error añadiendo al carrito', err);
            alert((err && err.description) || 'Error añadiendo al carrito');
          })
          .finally(() => {
            if (submitBtn) {
              submitBtn.classList.remove('processing');
              submitBtn.disabled = false;
            }
          });
      }, true);
    }

    /* ----------------------------------------------------------
       INTERCEPT QUICK BUY (data-quick-buy en tarjetas de producto)
    ---------------------------------------------------------- */

    _interceptQuickBuy() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quick-buy]');
        if (!btn) return;
        if (btn.disabled) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const variantId = btn.dataset.variantId;
        const quantity  = parseInt(btn.dataset.quantity, 10) || 1;
        if (!variantId) return;

        btn.disabled = true;
        btn.classList.add('loading');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity })
        })
          .then(r => {
            if (!r.ok) return r.json().then(d => Promise.reject(d));
            return fetch('/cart.js').then(r => r.json());
          })
          .then(cart => {
            this._updateHeaderDOM(cart);
            this.open();
          })
          .catch(err => {
            console.error('CartDrawer: error en quick buy', err);
            alert((err && err.description) || 'Error añadiendo al carrito');
          })
          .finally(() => {
            btn.disabled = false;
            btn.classList.remove('loading');
          });
      }, true);
    }

    /* ----------------------------------------------------------
       INTERCEPT CART LINKS IN HEADER
    ---------------------------------------------------------- */

    _interceptCartLinks() {
      // capture:true — se ejecuta antes que los listeners del tema
      document.addEventListener('click', (e) => {
        const cartLink = e.target.closest([
          '[data-cart-toggle]',
          '.site-header-cart-button',
          'a[href="/cart"]',
          'a[href="/es/cart"]',
          'a[href="/en/cart"]',
        ].join(','));

        if (!cartLink) return;
        if (this.drawer && this.drawer.contains(cartLink)) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        this.open();
      }, true); // capture:true
    }

    /* ----------------------------------------------------------
       EVENTS
    ---------------------------------------------------------- */

    _bindEvents() {
      this.closeBtn && this.closeBtn.addEventListener('click', () => this.close());
      this.overlay  && this.overlay.addEventListener('click',  () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
      });

      document.addEventListener('cart:open',    () => this.open());
      document.addEventListener('cart:refresh', () => {
        if (this.drawer.classList.contains('is-open')) this.fetchCart();
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawer = new CartDrawer();
  });

})();